import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Clock, RefreshCcw, MapPin, Sun } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'bestbuddy_geo';

/**
 * Training Windows widget — hour-by-hour analysis of today's forecast
 * to surface optimal outdoor-training time slots. Sits on the Fitness page
 * underneath the WeatherTipsCard.
 *
 * Rule-based, zero LLM cost.
 */
export const TrainingWindowsCard = () => {
    const { language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [windows, setWindows] = useState([]);
    const [summary, setSummary] = useState('');
    const [hours, setHours] = useState([]);
    const [needsPermission, setNeedsPermission] = useState(false);

    const isDe = language === 'de';

    const readCachedCoords = () => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (Date.now() - (parsed.ts || 0) > 12 * 60 * 60 * 1000) return null;
            return parsed.coords || null;
        } catch { return null; }
    };

    const fetchWindows = useCallback(async (coords) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/weather/training-windows`, {
                params: { lat: coords.lat, lon: coords.lon, language },
            });
            if (data?.error) {
                setWindows([]); setSummary('');
            } else {
                setWindows(data.windows || []);
                setSummary(data.summary || '');
                setHours(data.all_hours || []);
            }
        } catch {
            setWindows([]); setSummary('');
        } finally {
            setLoading(false);
        }
    }, [language]);

    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) { setLoading(false); return; }
        setNeedsPermission(false);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
                try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), coords })); } catch { /* ignore */ }
                fetchWindows(coords);
            },
            (err) => {
                setLoading(false);
                if (err.code === 1) setNeedsPermission(true);
            },
            { enableHighAccuracy: false, timeout: 7000, maximumAge: 60 * 60 * 1000 }
        );
    }, [fetchWindows]);

    useEffect(() => {
        const cached = readCachedCoords();
        if (cached) fetchWindows(cached);
        else requestLocation();
    }, [fetchWindows, requestLocation]);

    if (loading) {
        return (
            <Card data-testid="training-windows">
                <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
                    <Clock className="w-5 h-5 animate-pulse" />
                    {isDe ? 'Trainings-Fenster werden berechnet...' : 'Computing training windows...'}
                </CardContent>
            </Card>
        );
    }

    if (needsPermission) {
        return (
            <Card data-testid="training-windows">
                <CardContent className="p-4 flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold">
                            {isDe ? 'Standort freigeben für Trainings-Fenster' : 'Allow location for training windows'}
                        </p>
                        <Button size="sm" className="mt-2" onClick={requestLocation}>
                            {isDe ? 'Standort freigeben' : 'Allow location'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!summary && !windows.length) return null;

    const qualityChip = (q) => {
        if (q === 'optimal') return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: isDe ? 'Optimal' : 'Optimal' };
        if (q === 'good') return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: isDe ? 'Gut' : 'Good' };
        if (q === 'hard') return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: isDe ? 'Hart' : 'Hard' };
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: isDe ? 'Vermeiden' : 'Avoid' };
    };

    return (
        <Card data-testid="training-windows" className="overflow-hidden">
            <div className="bg-gradient-to-br from-blue-500/15 via-cyan-500/10 to-emerald-500/10 border-b px-4 py-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-bold text-muted-foreground">
                        {isDe ? 'Trainings-Fenster heute' : "Today's training windows"}
                    </p>
                    <p className="text-sm font-semibold leading-snug" data-testid="training-windows-summary">{summary}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={requestLocation} aria-label="Refresh">
                    <RefreshCcw className="w-4 h-4" />
                </Button>
            </div>
            {windows.length > 0 && (
                <CardContent className="p-0 divide-y">
                    {windows.map((w, i) => {
                        const chip = qualityChip(w.quality);
                        return (
                            <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`training-window-${i}`}>
                                <div className="flex-shrink-0 text-center min-w-[68px]">
                                    <p className="text-lg font-bold text-foreground leading-none">{w.label}</p>
                                    <p className="text-[11px] text-muted-foreground mt-1">{w.temp_range}</p>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className={`inline-block text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${chip.bg} ${chip.border} ${chip.text}`}>
                                        {chip.label}
                                    </span>
                                    <p className="text-xs text-muted-foreground mt-1.5 leading-snug">{w.note}</p>
                                </div>
                            </div>
                        );
                    })}
                </CardContent>
            )}
            {hours.length > 0 && (
                <div className="px-4 py-3 border-t bg-muted/20">
                    <div className="flex items-center gap-1 mb-1.5">
                        <Sun className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                            {isDe ? 'Stunden-Übersicht' : 'Hourly map'}
                        </p>
                    </div>
                    <div className="flex gap-0.5 h-7 rounded overflow-hidden" data-testid="training-hour-strip">
                        {hours.map((h) => {
                            const color = h.quality === 'optimal'
                                ? 'bg-emerald-500'
                                : h.quality === 'good'
                                    ? 'bg-amber-400'
                                    : h.quality === 'hard'
                                        ? 'bg-orange-500'
                                        : 'bg-red-500';
                            return (
                                <div
                                    key={h.hour}
                                    className={`flex-1 ${color} relative group`}
                                    title={`${h.hour}:00 — ${h.temp}°C — ${h.quality}`}
                                />
                            );
                        })}
                    </div>
                    <div className="flex justify-between mt-1 text-[9px] text-muted-foreground font-mono">
                        <span>{hours[0]?.hour}:00</span>
                        <span>{hours[Math.floor(hours.length/2)]?.hour}:00</span>
                        <span>{hours[hours.length-1]?.hour}:00</span>
                    </div>
                </div>
            )}
        </Card>
    );
};
